import axios from "axios"
import { saveAs } from "file-saver"

const apiUrl = "/api/"
const tacLiveApiUrl = String(process.env.VUE_APP_TAC_LIVE_API_URL || "https://app.wellcomefit.ch/api/").replace(/\/+$/, "") + "/"

function buildUrl(path) {
  return `${apiUrl}${path}`
}

function buildTacReadUrl(path) {
  const useLiveApi = process.env.NODE_ENV === "development" || String(process.env.VUE_APP_TAC_USE_LIVE_API || "").toLowerCase() === "true"
  const baseUrl = useLiveApi ? tacLiveApiUrl : apiUrl
  return `${baseUrl}${String(path || "").replace(/^\/+/, "")}`
}

function buildOptions(options = {}, signal = null) {
  if (!options.headers) {
    options.headers = {}
  }

  if (signal) options.signal = signal

  options.headers.Authorization = localStorage.getItem("token")
  return options
}

function blobToBase64(blob) {
  return new Promise((resolve, _) => {
    const reader = new FileReader()
    reader.onloadend = () => resolve(reader.result)
    reader.readAsDataURL(blob)
  })
}
export default {
  shiftManagement: {
    // Holt den Tagesplan + Tasks + Notizen
    fetchHistory(user) {
      let url = buildUrl(`appointments/history/${user}`)
      return axios.get(url, buildOptions())
    },
    fetchDailyView(dateStr) {
      let url = buildUrl(`studio-operation/daily-view/${dateStr}`)
      return axios.get(url, buildOptions())
    },
    fetchAppointmentFinderRange(startDate, endDate, excludeAppointmentId = null) {
      const excludeQuery = excludeAppointmentId ? `?excludeAppointmentId=${encodeURIComponent(excludeAppointmentId)}` : ""
      let url = buildUrl(`studio-operation/appointment-finder/${encodeURIComponent(startDate)}/${encodeURIComponent(endDate)}${excludeQuery}`)
      return axios.get(url, buildOptions()).then((res) => res.data)
    },
    searchAppointments(query, cursor = null, limit = 25, signal = null, includeTasks = false, period = "upcoming") {
      const params = [
        `q=${encodeURIComponent(query)}`,
        `limit=${encodeURIComponent(limit)}`,
        `includeTasks=${includeTasks === false ? "false" : "true"}`,
        `period=${period === "past" ? "past" : "upcoming"}`,
      ]
      if (cursor) params.push(`cursor=${encodeURIComponent(cursor)}`)
      const url = buildUrl(`studio-operation/appointment-search?${params.join("&")}`)
      return axios.get(url, buildOptions({}, signal)).then((res) => res.data)
    },
    fetchWeeklyView(startDate) {
      let url = buildUrl(`studio-operation/weekly-view/${startDate}`)
      return axios.get(url, buildOptions())
    },

    // TASKS (Ämtli)
    createTask(data) {
      // data: { date, time, title, description, assignedEmployee, ... }
      let url = buildUrl("studio-operation/task")
      return axios.post(url, data, buildOptions())
    },
    updateTask(id, data) {
      let url = buildUrl(`studio-operation/task/${id}`)
      return axios.put(url, data, buildOptions())
    },
    deleteTask(id, scope = "single") {
      let url = buildUrl(`studio-operation/task/${id}?scope=${scope}`)
      return axios.delete(url, buildOptions())
    },
    autoAssignTasks(data) {
      let url = buildUrl("studio-operation/auto-assign-tasks")
      return axios.post(url, data, buildOptions())
    },
    createTemplate(data) {
      let url = buildUrl("studio-operation/template")
      return axios.post(url, data, buildOptions())
    },
    deleteTemplate(id) {
      let url = buildUrl(`studio-operation/template/${id}`)
      return axios.delete(url, buildOptions())
    },
    updateTemplate(id, data) {
      let url = buildUrl(`studio-operation/template/${id}`)
      return axios.put(url, data, buildOptions())
    },
    markTemplateAsDone(data) {
      let url = buildUrl("studio-operation/template-completion")
      return axios.post(url, data, buildOptions())
    },
    removeTemplateDone(id) {
      let url = buildUrl(`studio-operation/template-completion/${id}`)
      return axios.delete(url, buildOptions())
    },

    // APPOINTMENTS
    createAppointment(data) {
      let url = buildUrl("studio-operation/appointment")
      return axios.post(url, data, buildOptions()).then((res) => res.data)
    },
    fetchTacCredits(customerId, { serviceType, appointmentDate, startTime, templateId, templateTitle } = {}) {
      const params = new URLSearchParams()
      if (serviceType) params.set("serviceType", serviceType)
      if (appointmentDate) params.set("appointmentDate", appointmentDate)
      if (startTime) params.set("startTime", startTime)
      if (templateId) params.set("templateId", templateId)
      if (templateTitle) params.set("templateTitle", templateTitle)
      const url = buildTacReadUrl(`studio-operation/tac-credits/${encodeURIComponent(customerId)}?${params.toString()}`)
      return axios.get(url, buildOptions()).then((res) => res.data)
    },
    requestTac(type, endpoint, body = null) {
      const url = buildTacReadUrl("tac/api")
      return axios
        .post(
          url,
          {
            type: String(type || "post").toLowerCase(),
            endpoint: String(endpoint || "").replace(/^\/+/, ""),
            body,
          },
          buildOptions(),
        )
        .then((res) => res.data)
    },
    updateAppointment(id, data) {
      let url = buildUrl(`studio-operation/appointment/${id}`)
      return axios.put(url, data, buildOptions())
    },
    applyAppointmentClipboard(data) {
      let url = buildUrl("studio-operation/appointment/clipboard")
      return axios.post(url, data, buildOptions()).then((res) => res.data)
    },
    fetchAppointmentChangeHistory(month, limit = 80) {
      let url = buildUrl(`studio-operation/appointment-history?month=${encodeURIComponent(month)}&limit=${encodeURIComponent(limit)}`)
      return axios.get(url, buildOptions()).then((res) => res.data)
    },
    fetchAppointmentAuditHistory(appointmentId, limit = 50) {
      let url = buildUrl(`studio-operation/appointment/${encodeURIComponent(appointmentId)}/history?limit=${encodeURIComponent(limit)}`)
      return axios.get(url, buildOptions()).then((res) => res.data)
    },
    deleteAppointment(id, reason, scope = "single") {
      let url = buildUrl(`studio-operation/appointment/${id}`)
      return axios.put(url, { deleted: true, deleteReason: reason || "", scope }, buildOptions())
    },

    // NOTES (Tages-Notizen)
    createNote(data) {
      // data: { date, targetEmployee, content }
      let url = buildUrl("studio-operation/note")
      return axios.post(url, data, buildOptions())
    },
    deleteNote(id) {
      let url = buildUrl(`studio-operation/note/${id}`)
      return axios.delete(url, buildOptions())
    },
    // api.shiftManagement.fetchPlan(studioId, start, end) -> { shifts: [...] }
    fetchPlan(studioId, startDate, endDate) {
      let url = buildUrl(`shift-management/plan/fetch/${studioId}/${startDate}/${endDate}`)
      return axios.get(url, buildOptions()).then((res) => res.data)
    },
    fetchWorkHoursSummaries(studioIds, startDate, endDate, settingsOverrides) {
      let url = buildUrl("shift-management/work-hours/summary")
      const payload = { studioIds, startDate, endDate }
      if (settingsOverrides && Object.keys(settingsOverrides).length > 0) payload.settingsOverrides = settingsOverrides
      return axios.post(url, payload, buildOptions()).then((res) => res.data)
    },
    fetchNotes(studioId, startDate, endDate) {
      let url = buildUrl(`shift-management/plan/notes/${studioId}/${startDate}/${endDate}`)
      return axios.get(url, buildOptions()).then((res) => res.data)
    },
    generateFromTemplate(studioId, startDate, endDate) {
      let url = buildUrl("plan/generate-from-template")
      return axios.post(url, { studio: studioId, startDate, endDate }, buildOptions()).then((res) => res.data)
    },
    assignEmployeesToPlan(studioId, startDate, endDate, planningSettings, confirmedEmployeeUserIds, planningStrategy = "rotation") {
      let url = buildUrl("shift-management/plan/assign-employees")
      const payload = { studio: studioId, startDate, endDate, planningSettings, confirmedEmployeeUserIds, planningStrategy }
      return axios.post(url, payload, buildOptions()).then((res) => res.data)
    },
    resetPlanAssignments(studioId, startDate, endDate) {
      let url = buildUrl("shift-management/plan/reset")
      const payload = { studio: studioId, startDate, endDate }
      return axios.post(url, payload, buildOptions()).then((res) => res.data)
    },
    deleteUnassignedShifts(studioId, startDate, endDate) {
      let url = buildUrl("shift-management/plan/delete-unassigned")
      const payload = { studio: studioId, startDate, endDate }
      return axios.post(url, payload, buildOptions()).then((res) => res.data)
    },
    updateStudioOffset(studioId, offset) {
      let url = buildUrl("shift-management/studio/offset")
      return axios.put(url, { studioId, offset }, buildOptions()).then((res) => res.data)
    },
    createSpecialDay(data) {
      let url = buildUrl("shift-management/plan/special-day")
      return axios.post(url, data, buildOptions()).then((res) => res.data)
    },
    deleteSpecialDay(studioId, date) {
      let url = buildUrl("shift-management/plan/special-day/" + studioId + "/" + date)
      return axios.delete(url, buildOptions()).then((res) => res.data)
    },
    createManualShift(data) {
      let url = buildUrl("shift-management/shift")
      return axios.post(url, data, buildOptions())
    },

    updateShift(shiftId, data) {
      let url = buildUrl(`shift-management/shift/${shiftId}`)
      return axios.put(url, data, buildOptions())
    },
    fetchCandidates(shiftId, planningStrategy = "rotation") {
      let url = buildUrl(`shift-management/shift/${shiftId}/candidates?planningStrategy=${planningStrategy}`)
      return axios.get(url, buildOptions()).then((res) => res.data)
    },

    // 3. SCHICHT LÖSCHEN
    deleteShift(shiftId) {
      let url = buildUrl(`shift-management/shift/${shiftId}`)
      return axios.delete(url, buildOptions())
    },
    generatePlanFromTemplate(studioId, startDate, endDate) {
      let url = buildUrl("plan/generate-from-template")
      const payload = { studio: studioId, startDate, endDate }
      return axios.post(url, payload, buildOptions()).then((res) => res.data)
    },
    fetchWeeklyTemplate(studioId) {
      let url = buildUrl(`shift-management/template/weekly/${studioId}`)
      return axios.get(url, buildOptions()).then((res) => res.data)
    },
    saveWeeklyTemplate(studioId, templateData) {
      let url = buildUrl(`shift-management/template/weekly/${studioId}`)
      return axios.put(url, { template: templateData }, buildOptions()).then((res) => res.data)
    },
    fetchSettings(studioId) {
      let url = buildUrl("shift-management/settings/fetch/" + studioId)
      return axios.get(url, buildOptions()).then((res) => res.data)
    },
    saveEmployeeSortOrder(employeeIds) {
      let url = buildUrl("shift-management/employee/sort-order")
      return axios.put(url, { employeeIds }, buildOptions()).then((res) => res.data)
    },
    updateEmployeePlanningSettings(employeeId, settings) {
      let url = buildUrl(`employee/website/planning-settings/${employeeId}`)
      return axios.put(url, settings, buildOptions()).then((res) => res.data)
    },
    createOrUpdateSettings(data) {
      let url = buildUrl("shift-management/settings/create-or-update")
      return axios.post(url, data, buildOptions()).then((res) => res.data)
    },
    deleteSetting(type, id) {
      let url = buildUrl(`shift-management/settings/delete/${type}/${id}`)
      return axios.delete(url, buildOptions()).then((res) => res.data)
    },

    fetchShiftManagementCheckinData() {
      let url = buildUrl("shift-management/checkin/data")
      return axios.get(url, buildOptions()).then((res) => res.data)
    },
    fetchAllHolidayEntries() {
      let url = buildUrl("timetracker/holidays/all")
      return axios.get(url, buildOptions()).then((res) => res.data)
    },
    fetchShiftManagementEmployee(studioId) {
      let url = buildUrl("shift-management/employee/data" + (studioId ? `/${studioId}` : ""))
      return axios.get(url, buildOptions()).then((res) => res.data)
    },
    createShiftPlanEntry(data) {
      let url = buildUrl("shift-management/plan/create")
      return axios.post(url, data, buildOptions()).then((res) => res.data)
    },

    fetchShiftPlans(studioId, startDate, endDate) {
      let url = buildUrl(`shift-management/plan/fetch/${studioId}/${startDate}/${endDate}`)
      return axios.get(url, buildOptions()).then((res) => res.data)
    },

    fetchPlansOverview(studioId) {
      let url = buildUrl(`shift-management/plans/overview/${studioId}`)
      return axios.get(url, buildOptions()).then((res) => res.data)
    },

    deleteShiftPlanEntry(id) {
      let url = buildUrl(`shift-management/plan/delete/${id}`)
      return axios.delete(url, buildOptions()).then((res) => res.data)
    },

    // *** NEU: FEIERTAGE ***
    fetchPublicHolidays(studioId, startDate, endDate) {
      let url = buildUrl(`shift-management/public-holidays/${studioId}/${startDate}/${endDate}`)
      return axios.get(url, buildOptions()).then((res) => res.data)
    },

    // *** NEU: PLANNING SETTINGS (ersetzt localStorage) ***
    savePlanningSettings(employeeId, studioId, settings) {
      let url = buildUrl(`shift-management/planning-settings/${employeeId}/${studioId}`)
      return axios.put(url, settings, buildOptions()).then((res) => res.data)
    },
    loadPlanningSettings(employeeId, studioId) {
      let url = buildUrl(`shift-management/planning-settings/${employeeId}/${studioId}`)
      return axios.get(url, buildOptions()).then((res) => res.data)
    },
    saveMonthlyPlanningSettings(employeeId, studioId, month, settings) {
      let url = buildUrl(`shift-management/planning-settings/month/${employeeId}/${studioId}/${month}`)
      return axios.put(url, settings, buildOptions()).then((res) => res.data)
    },
    loadMonthlyPlanningSettings(employeeId, studioId, month) {
      let url = buildUrl(`shift-management/planning-settings/month/${employeeId}/${studioId}/${month}`)
      return axios.get(url, buildOptions()).then((res) => res.data)
    },

    // *** NEU: VORMONATS-KONTEXT ***
    fetchPreviousMonthContext(studioId, monthStartDate) {
      let url = buildUrl(`shift-management/plan/previous-context/${studioId}/${monthStartDate}`)
      return axios.get(url, buildOptions()).then((res) => res.data)
    },

    // *** NEU: EMPLOYEE PLANNING VIEW ***
    fetchEmployeeScheduleDaily(dateStr) {
      let url = buildUrl(`shift-management/employee-schedule/daily/${dateStr}`)
      return axios
        .get(url, buildOptions())
        .then((res) => res.data)
        .catch(() => null)
    },
    fetchEmployeeScheduleMonthly(monthStr) {
      let url = buildUrl(`shift-management/employee-schedule/monthly/${monthStr}`)
      return axios
        .get(url, buildOptions())
        .then((res) => res.data)
        .catch(() => null)
    },
    downloadEmployeeSchedule(monthStr) {
      let url = buildUrl(`shift-management/employee-schedule/download/${monthStr}`)
      return axios
        .get(url, { ...buildOptions(), responseType: "blob" })
        .then((res) => res.data)
        .catch(() => null)
    },

    // WORKCALENDAR STATISTIK DASHBOARD
    fetchWorkCalendarStatistics(data) {
      // data: { studios: [id, ...], range: ["YYYY-MM-DD", "YYYY-MM-DD"] }
      let url = buildUrl("studio-operation/statistics-v2")
      return axios.post(url, data, buildOptions()).then((res) => res.data)
    },
    updateAbsenceMode(data) {
      let url = buildUrl("shift-management/update-absence-mode")
      return axios.post(url, data, buildOptions()).then((res) => res.data)
    },
    createSickFromTransfer(data) {
      // data: { employee, date }
      let url = buildUrl("shift-management/sick/create-from-transfer")
      return axios.post(url, data, buildOptions()).then((res) => res.data)
    },
    fetchSeriesPreview(data) {
      let url = buildUrl("studio-operation/series-preview")
      return axios.post(url, data, buildOptions()).then((res) => res.data)
    },
    batchCreateSeries(data) {
      let url = buildUrl("studio-operation/batch-create")
      return axios.post(url, data, buildOptions()).then((res) => res.data)
    },
    fetchActiveSeries() {
      let url = buildUrl("studio-operation/active-series")
      return axios.get(url, buildOptions()).then((res) => res.data)
    },
    fetchTasksDashboard(startDate, endDate) {
      let url = buildUrl(`studio-operation/tasks-dashboard/${startDate}/${endDate}`)
      return axios.get(url, buildOptions())
    },
    publishTasks(data) {
      let url = buildUrl("studio-operation/tasks/publish")
      return axios.post(url, data, buildOptions())
    },
  },
}
